// Performance Dashboard Configuration
export const PERFORMANCE_CONFIG = {
  // Default refresh intervals (in milliseconds)
  REFRESH_INTERVALS: {
    FAST: 1000,    // 1 second
    NORMAL: 5000,  // 5 seconds
    SLOW: 10000,   // 10 seconds
    VERY_SLOW: 30000, // 30 seconds
  },

  // Default refresh interval
  DEFAULT_REFRESH_INTERVAL: 5000,

  // Alert thresholds
  ALERT_THRESHOLDS: {
    EVENT_LOOP_LATENCY: {
      WARNING: 50,   // ms
      CRITICAL: 100, // ms
    },
    MEMORY_USAGE: {
      WARNING: 70,   // percentage
      CRITICAL: 80,  // percentage
    },
    CPU_LOAD: {
      WARNING: 3.0,  // load average
      CRITICAL: 5.0, // load average
    },
    CPU_USAGE: {
      WARNING: 70,   // percentage
      CRITICAL: 90,  // percentage
    },
  },

  // API endpoints
  API_ENDPOINTS: {
    PERFORMANCE: '/performance',
    HEALTH: '/health',
    RESOURCES: '/resources',
  },

  // Chart configuration
  CHART_CONFIG: {
    COLORS: {
      PRIMARY: '#1890ff',
      SUCCESS: '#52c41a',
      WARNING: '#faad14',
      ERROR: '#ff4d4f',
      INFO: '#13c2c2',
      PURPLE: '#722ed1',
      ORANGE: '#fa8c16',
    },
    GRID_COLORS: {
      X: 'rgba(0, 0, 0, 0.1)',
      Y: 'rgba(0, 0, 0, 0.1)',
    },
    BACKGROUND_COLORS: {
      PRIMARY: 'rgba(24, 144, 255, 0.1)',
      SUCCESS: 'rgba(82, 196, 26, 0.1)',
      WARNING: 'rgba(250, 173, 20, 0.1)',
      ERROR: 'rgba(255, 77, 79, 0.1)',
    },
    BORDER_WIDTH: 2,
    POINT_RADIUS: 4,
    POINT_HOVER_RADIUS: 6,
  },

  // Theme settings
  THEME: {
    LIGHT: {
      BACKGROUND: '#ffffff',
      CARD_BACKGROUND: '#fafafa',
      TEXT_PRIMARY: '#262626',
      TEXT_SECONDARY: '#8c8c8c',
      BORDER: '#d9d9d9',
      SHADOW: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    DARK: {
      BACKGROUND: '#141414',
      CARD_BACKGROUND: '#1f1f1f',
      TEXT_PRIMARY: '#ffffff',
      TEXT_SECONDARY: '#a6a6a6',
      BORDER: '#434343',
      SHADOW: '0 2px 8px rgba(0, 0, 0, 0.3)',
    },
  },

  // Grid layout settings
  GRID_LAYOUT: {
    COLUMNS: {
      MOBILE: 1,
      TABLET: 2,
      DESKTOP: 3,
      LARGE_DESKTOP: 4,
    },
    GAP: 16,
    CARD_PADDING: 24,
  },

  // Performance settings
  PERFORMANCE: {
    VIRTUAL_SCROLLING_THRESHOLD: 100,
    CHART_ANIMATION_DURATION: 300,
    DEBOUNCE_DELAY: 300,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
  },

  // Export settings
  EXPORT: {
    CSV_DELIMITER: ',',
    DATE_FORMAT: 'YYYY-MM-DD HH:mm:ss',
    FILE_NAMES: {
      PERFORMANCE: 'performance-metrics',
      HEALTH: 'health-status',
      RESOURCES: 'resource-usage',
      COMBINED: 'performance-dashboard',
    },
  },

  // Status indicators
  STATUS: {
    HEALTHY: {
      color: '#52c41a',
      text: 'Healthy',
      icon: 'check-circle',
    },
    WARNING: {
      color: '#faad14',
      text: 'Warning',
      icon: 'exclamation-circle',
    },
    CRITICAL: {
      color: '#ff4d4f',
      text: 'Critical',
      icon: 'close-circle',
    },
    UNKNOWN: {
      color: '#8c8c8c',
      text: 'Unknown',
      icon: 'question-circle',
    },
  },

  // Memory units
  MEMORY_UNITS: {
    BYTES: 'B',
    KILOBYTES: 'KB',
    MEGABYTES: 'MB',
    GIGABYTES: 'GB',
    TERABYTES: 'TB',
  },

  // Time units
  TIME_UNITS: {
    MILLISECONDS: 'ms',
    SECONDS: 's',
    MINUTES: 'm',
    HOURS: 'h',
    DAYS: 'd',
  },

  // Chart data limits
  CHART_DATA_LIMITS: {
    MAX_POINTS: 100,
    MIN_POINTS: 10,
    DEFAULT_POINTS: 50,
  },

  // Responsive breakpoints
  BREAKPOINTS: {
    MOBILE: 768,
    TABLET: 1024,
    DESKTOP: 1200,
    LARGE_DESKTOP: 1600,
  },
};

// Utility functions
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const formatDuration = (milliseconds) => {
  if (milliseconds < 1000) {
    return `${milliseconds}ms`;
  } else if (milliseconds < 60000) {
    return `${(milliseconds / 1000).toFixed(1)}s`;
  } else if (milliseconds < 3600000) {
    return `${(milliseconds / 60000).toFixed(1)}m`;
  } else {
    return `${(milliseconds / 3600000).toFixed(1)}h`;
  }
};

export const formatUptime = (seconds) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const getStatusColor = (status) => {
  return PERFORMANCE_CONFIG.STATUS[status?.toUpperCase()]?.color || PERFORMANCE_CONFIG.STATUS.UNKNOWN.color;
};

export const getStatusIcon = (status) => {
  return PERFORMANCE_CONFIG.STATUS[status?.toUpperCase()]?.icon || PERFORMANCE_CONFIG.STATUS.UNKNOWN.icon;
};

export const getStatusText = (status) => {
  return PERFORMANCE_CONFIG.STATUS[status?.toUpperCase()]?.text || PERFORMANCE_CONFIG.STATUS.UNKNOWN.text;
};

export const isMobile = () => {
  return window.innerWidth < PERFORMANCE_CONFIG.BREAKPOINTS.MOBILE;
};

export const isTablet = () => {
  return window.innerWidth >= PERFORMANCE_CONFIG.BREAKPOINTS.MOBILE && 
         window.innerWidth < PERFORMANCE_CONFIG.BREAKPOINTS.TABLET;
};

export const isDesktop = () => {
  return window.innerWidth >= PERFORMANCE_CONFIG.BREAKPOINTS.TABLET;
};

export default PERFORMANCE_CONFIG;
